import { ServerCog } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import { Button, EmptyState, Spinner } from '@aeris/ui'

import { ServerSetup } from '../components/server-setup.tsx'
import {
  checkDesktopUi,
  readDesktopUi,
  resetDesktopUi,
  type DesktopUiStatus,
} from '../lib/desktop-ui.ts'
import { readShellConfig, type ShellConfig } from '../lib/shell-config.ts'

/**
 * Trocar o servidor desta máquina, depois da primeira vez.
 *
 * O mesmo formulário do primeiro dia — a pergunta não mudou, e uma segunda
 * versão dela seria uma segunda chance de as duas discordarem. O que muda é o
 * caminho até aqui: lá, o produto empurra; aqui, alguém procurou.
 *
 * Fora do shell de propósito, como o login: quem está trocando de servidor
 * pode estar apontando para um que não responde, e um menu lateral cheio de
 * módulos que não abrem seria uma promessa falsa.
 *
 * Num navegador comum a rota existe e diz que não há o que configurar — o
 * endereço do servidor, ali, é a barra de endereço.
 */
export const ServidorPage = (): ReactNode => {
  const [config, setConfig] = useState<ShellConfig | null | undefined>(
    undefined
  )
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    const controller = new AbortController()

    void readShellConfig(controller.signal).then((answer) => {
      if (!controller.signal.aborted) setConfig(answer)
    })

    return () => {
      controller.abort()
    }
  }, [])

  if (config === undefined) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-(--surface-base)">
        <Spinner />
      </div>
    )
  }

  if (config === null) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-(--surface-base) p-5">
        <EmptyState
          icon={<ServerCog aria-hidden="true" className="h-8 w-8" />}
          title="Nada a configurar aqui"
          description="Esta tela existe no aplicativo instalado, para dizer a ele onde fica o servidor da sua empresa. No navegador, o servidor é o endereço que você já digitou na barra."
        />
      </div>
    )
  }

  if (saved) {
    /*
     * Recarregar, e não navegar.
     *
     * Trocar de servidor troca a conta, as permissões e a sessão inteira. O
     * cache do React Query em memória ainda guarda as respostas do servidor
     * anterior, e nenhuma delas vale mais. Um recarregamento é a única forma
     * honesta de dizer isso ao aplicativo.
     */
    window.location.assign('/')
    return null
  }

  return (
    <>
      <ServerSetup
        config={config}
        onConfigured={() => {
          setSaved(true)
        }}
      />
      <DesktopUiPanel />
    </>
  )
}

/**
 * Qual tela este computador está usando, e a saída de emergência (F213).
 *
 * O desktop baixa a tela do servidor (ADR-0040). Se uma versão baixada der
 * problema, "Voltar para a tela do instalador" desfaz na hora, sem depender da
 * volta automática nem de reinstalar.
 */
const DesktopUiPanel = (): ReactNode => {
  const [status, setStatus] = useState<DesktopUiStatus | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    void readDesktopUi(controller.signal).then((answer) => {
      if (!controller.signal.aborted) setStatus(answer)
    })
    return () => {
      controller.abort()
    }
  }, [])

  if (status === null) return null

  return (
    <div className="fixed inset-x-0 bottom-0 flex flex-wrap items-center justify-center gap-3 border-t border-(--border-subtle) bg-(--surface-raised) px-4 py-2 text-(length:--text-sm) text-(--text-secondary)">
      <span>
        Aeris {status.shell} · tela{' '}
        {status.latest === null
          ? 'do instalador'
          : `baixada do servidor (${status.latest})`}
      </span>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          void checkDesktopUi().then((answer) => {
            if (answer !== null) setStatus(answer)
          })
        }}
      >
        Procurar atualização
      </Button>
      {status.latest === null ? null : (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            void resetDesktopUi().then(() => {
              window.location.assign('/')
            })
          }}
        >
          Voltar para a tela do instalador
        </Button>
      )}
    </div>
  )
}
